// ModuleContentManager.jsx - Gestion du contenu des modules - Version Tailwind CSS
// Support PDF, VIDEO, JSON, JSON_RICH
import React, { useState, useEffect, useRef } from 'react';
import { APP_CONFIG } from '../../config/appConfig';
import { useNavigate } from 'react-router-dom';

export default function ModuleContentManager({
    moduleId,
    token,
    initialType = 'PDF',
    initialPdfUrl = '',
    initialVideoUrl = '',
    initialJsonContent = '',
    onContentUpdate,
    module = null
}) {
    const navigate = useNavigate();
    const [contentType, setContentType] = useState(initialType);
    const [pdfFile, setPdfFile] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(initialPdfUrl);
    const [pdfFileName, setPdfFileName] = useState('');
    const [fileExists, setFileExists] = useState(false);
    const [videoFile, setVideoFile] = useState(null);
    const [videoUrl, setVideoUrl] = useState(initialVideoUrl);
    const [videoFileName, setVideoFileName] = useState('');
    const [videoFileExists, setVideoFileExists] = useState(false);
    const [jsonContent, setJsonContent] = useState(initialJsonContent || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);
    const videoInputRef = useRef(null);

    useEffect(() => {
        if ((contentType === 'JSON' || contentType === 'JSON_RICH') && moduleId) {
            fetchJsonContent();
        }
        if (contentType === 'PDF' && moduleId) {
            fetchPdfInfo();
        }
        if (contentType === 'VIDEO' && moduleId) {
            fetchVideoInfo();
        }
    }, [contentType, moduleId]);

    const fetchPdfInfo = async () => {
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/pdf-url`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.fileExists === 'true') {
                    setPdfUrl(data.pdfUrl);
                    setPdfFileName(data.fileName || '');
                    setFileExists(true);
                } else {
                    setPdfUrl('');
                    setPdfFileName('');
                    setFileExists(false);
                }
            }
        } catch (err) {
            console.error("❌ [MODULE CONTENT] Erreur récupération info PDF:", err);
        }
    };

    const fetchVideoInfo = async () => {
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/video-url`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.fileExists === 'true') {
                    setVideoUrl(data.videoUrl);
                    setVideoFileName(data.fileName || '');
                    setVideoFileExists(true);
                } else {
                    setVideoUrl('');
                    setVideoFileName('');
                    setVideoFileExists(false);
                }
            }
        } catch (err) {
            console.error("❌ [MODULE CONTENT] Erreur récupération info vidéo:", err);
        }
    };

    const fetchJsonContent = async () => {
        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/json-content`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (typeof data === 'object' && data !== null) {
                    setJsonContent(JSON.stringify(data, null, 2));
                } else {
                    setJsonContent(data || '');
                }
            }
        } catch (err) {
            console.error("❌ [MODULE CONTENT] Erreur chargement JSON:", err);
        }
    };

    const handlePdfUpload = async (e) => {
        e.preventDefault();

        if (!pdfFile) {
            setError("Veuillez sélectionner un fichier PDF");
            return;
        }

        if (pdfFile.type !== 'application/pdf' && !pdfFile.name.toLowerCase().endsWith('.pdf')) {
            setError("Le fichier doit être un PDF");
            return;
        }

        if (pdfFile.size > 50 * 1024 * 1024) {
            setError("Le fichier ne doit pas dépasser 50MB");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', pdfFile);

        try {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(progress);
                }
            });

            const response = await new Promise((resolve, reject) => {
                xhr.open('POST', `${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/pdf`, true);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            resolve(JSON.parse(xhr.responseText));
                        } catch (e) {
                            console.error(e);
                            reject(new Error('Réponse invalide du serveur'));
                        }
                    } else {
                        reject(new Error(`Erreur HTTP ${xhr.status}`));
                    }
                };
                xhr.onerror = () => reject(new Error('Erreur réseau'));
                xhr.send(formData);
            });

            if (response && response.status === 'SUCCESS') {
                await fetchPdfInfo();
                setSuccess("✅ PDF uploadé avec succès");
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                setPdfFile(null);
                if (onContentUpdate) onContentUpdate();
            } else {
                setError(response?.error || "Erreur lors de l'upload du PDF");
            }
        } catch (err) {
            console.error("❌ [MODULE CONTENT] Erreur upload PDF:", err);
            setError(err.message || "Erreur technique lors de l'upload");
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const handleVideoUpload = async (e) => {
        e.preventDefault();

        if (!videoFile) {
            setError("Veuillez sélectionner un fichier vidéo");
            return;
        }

        const allowedTypes = ['video/mp4', 'video/webm'];
        if (!allowedTypes.includes(videoFile.type)) {
            setError("Le fichier doit être une vidéo au format mp4 ou webm");
            return;
        }

        if (videoFile.size > 500 * 1024 * 1024) {
            setError("Le fichier ne doit pas dépasser 500MB");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', videoFile);

        try {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(progress);
                }
            });

            const response = await new Promise((resolve, reject) => {
                xhr.open('POST', `${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/video/upload`, true);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            resolve(JSON.parse(xhr.responseText));
                        } catch (e) {
                            console.error(e);
                            reject(new Error('Réponse invalide du serveur'));
                        }
                    } else {
                        reject(new Error(`Erreur HTTP ${xhr.status}`));
                    }
                };
                xhr.onerror = () => reject(new Error('Erreur réseau'));
                xhr.send(formData);
            });

            if (response && response.status === 'SUCCESS') {
                await fetchVideoInfo();
                setSuccess("✅ Vidéo uploadée avec succès");
                if (videoInputRef.current) {
                    videoInputRef.current.value = '';
                }
                setVideoFile(null);
                if (onContentUpdate) onContentUpdate();
            } else {
                setError(response?.error || "Erreur lors de l'upload de la vidéo");
            }
        } catch (err) {
            console.error("❌ [MODULE CONTENT] Erreur upload vidéo:", err);
            setError(err.message || "Erreur technique lors de l'upload");
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const handleJsonSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            let jsonData;
            try {
                jsonData = JSON.parse(jsonContent);
            } catch (parseError) {
                console.error(parseError);
                setError("❌ Format JSON invalide. Vérifiez la syntaxe.");
                setLoading(false);
                return;
            }

            // Déterminer le typeModule en fonction de contentType
            const typeParam = contentType === 'JSON_RICH' ? 'JSON_RICH' : 'JSON';

            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/json-content?typeModule=${typeParam}`, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(jsonData)
            });

            if (response.ok) {
                setSuccess(`✅ Contenu ${typeParam} sauvegardé avec succès`);
                if (onContentUpdate) onContentUpdate();
            } else {
                const data = await response.json();
                setError(data.error || "Erreur lors de la sauvegarde");
            }
        } catch (err) {
            console.error("❌ [MODULE CONTENT] Erreur sauvegarde JSON:", err);
            setError("Erreur technique lors de la sauvegarde");
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePdf = async () => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce PDF ?")) return;

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/pdf`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (response.ok) {
                setPdfUrl('');
                setPdfFileName('');
                setFileExists(false);
                setSuccess("✅ PDF supprimé avec succès");
                if (onContentUpdate) onContentUpdate();
            } else {
                const data = await response.json();
                setError(data.error || "Erreur lors de la suppression");
            }
        } catch (err) {
            console.error("❌ [MODULE CONTENT] Erreur suppression PDF:", err);
            setError("Erreur technique lors de la suppression");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteVideo = async () => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette vidéo ?")) return;

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/modules/${moduleId}/video`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json"
                }
            });

            if (response.ok) {
                setVideoUrl('');
                setVideoFileName('');
                setVideoFileExists(false);
                setSuccess("✅ Vidéo supprimée avec succès");
                if (onContentUpdate) onContentUpdate();
            } else {
                const data = await response.json();
                setError(data.error || "Erreur lors de la suppression");
            }
        } catch (err) {
            console.error("❌ [MODULE CONTENT] Erreur suppression vidéo:", err);
            setError("Erreur technique lors de la suppression");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!pdfUrl) return;

        try {
            const response = await fetch(pdfUrl, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = pdfFileName || `module_${moduleId}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                setError("Erreur lors du téléchargement du PDF");
            }
        } catch (err) {
            console.error("❌ [MODULE CONTENT] Erreur téléchargement PDF:", err);
            setError("Erreur technique lors du téléchargement");
        }
    };

    const validateJson = () => {
        try {
            JSON.parse(jsonContent);
            setSuccess("✅ JSON valide");
            setTimeout(() => setSuccess(null), 3000);
        } catch (e) {
            setError("❌ JSON invalide: " + e.message);
            setTimeout(() => setError(null), 3000);
        }
    };

    const handleOpenDemo = () => {
        if (!module) {
            setError("❌ Informations du module manquantes");
            return;
        }

        let contentData = null;
        let contentTypeDetected = 'UNKNOWN';

        if (fileExists && pdfUrl) {
            contentTypeDetected = 'PDF';
            contentData = { pdfUrl: pdfUrl, fileName: pdfFileName };
        } else if (videoFileExists && videoUrl) {
            contentTypeDetected = 'VIDEO';
            contentData = { videoUrl: videoUrl, fileName: videoFileName };
        } else if (jsonContent) {
            try {
                const parsed = JSON.parse(jsonContent);
                if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].enonce) {
                    contentTypeDetected = 'EVALUATION';
                    contentData = parsed;
                } else if (parsed.titre && parsed.modules) {
                    contentTypeDetected = 'JSON';
                    contentData = parsed;
                } else {
                    contentTypeDetected = 'JSON';
                    contentData = parsed;
                }
            } catch (e) {
                console.error(e);
                contentTypeDetected = 'TEXT';
                contentData = jsonContent;
            }
        }

        if (!contentData) {
            setError("❌ Aucun contenu à visualiser en démo");
            return;
        }

        navigate('/demo', {
            state: {
                module: {
                    id: module.id,
                    titre: module.titre || 'Module sans titre',
                    description: module.description || '',
                    type: module.type || 'FORMATION',
                    ordre: module.ordre || 0,
                    formationId: module.formationId
                },
                content: {
                    type: contentTypeDetected,
                    data: contentData,
                    pdfUrl: pdfUrl,
                    videoUrl: videoUrl,
                    jsonContent: jsonContent
                },
                token: token,
                mode: 'demo'
            }
        });
    };

    return (
        <div className="space-y-4">
            {/* Sélecteur de type de contenu */}
            <div className="flex gap-2 border-b border-gray-200 pb-3 flex-wrap">
                <button
                    onClick={() => setContentType('PDF')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        contentType === 'PDF'
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    📄 PDF
                </button>
                <button
                    onClick={() => setContentType('VIDEO')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        contentType === 'VIDEO'
                            ? 'bg-rose-600 text-white hover:bg-rose-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    🎬 Vidéo
                </button>
                <button
                    onClick={() => setContentType('JSON')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        contentType === 'JSON'
                            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    📝 JSON
                </button>
                <button
                    onClick={() => setContentType('JSON_RICH')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        contentType === 'JSON_RICH'
                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    📝 JSON_RICH
                </button>
            </div>

            {/* Messages */}
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error}
                </div>
            )}
            {success && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-600 text-sm">
                    {success}
                </div>
            )}

            {/* ======================================== */}
            {/* CONTENU PDF */}
            {/* ======================================== */}
            {contentType === 'PDF' && (
                <div className="space-y-4">
                    {fileExists && pdfUrl && (
                        <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-3xl">📄</span>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-700">
                                            PDF disponible
                                        </p>
                                        {pdfFileName && (
                                            <p className="text-xs text-gray-500 font-mono">
                                                📁 {pdfFileName}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <button
                                        onClick={handleDownloadPdf}
                                        className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-1"
                                    >
                                        ⬇️ Télécharger
                                    </button>
                                    <a
                                        href={pdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-1"
                                    >
                                        📖 Lire le PDF
                                    </a>
                                    <button
                                        onClick={handleDeletePdf}
                                        disabled={loading}
                                        className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                    >
                                        🗑️ Supprimer
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handlePdfUpload} className="space-y-3">
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-green-500 transition-colors">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={(e) => {
                                    setPdfFile(e.target.files[0]);
                                    setError(null);
                                }}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-600 file:text-white hover:file:bg-green-700 cursor-pointer"
                            />
                            <p className="text-xs text-gray-400 mt-2">
                                Glissez-déposez votre PDF ou cliquez pour sélectionner (max 50MB)
                            </p>
                            {pdfFile && (
                                <p className="text-xs text-emerald-600 mt-2">
                                    ✅ Fichier sélectionné : {pdfFile.name} ({Math.round(pdfFile.size / 1024)} KB)
                                </p>
                            )}
                        </div>

                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                        className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Upload: {uploadProgress}%
                                </p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || !pdfFile}
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Upload en cours...' : '📤 Uploader le PDF'}
                        </button>
                    </form>

                    <div className="bg-blue-50 p-3 rounded-xl border border-blue-200">
                        <p className="text-xs text-blue-700">
                            💡 Le PDF sera stocké et accessible via une URL de lecture dynamique.
                        </p>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* CONTENU VIDÉO */}
            {/* ======================================== */}
            {contentType === 'VIDEO' && (
                <div className="space-y-4">
                    {videoFileExists && videoUrl && (
                        <div className="bg-rose-50 p-4 rounded-xl border border-rose-200 space-y-3">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-3xl">🎬</span>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-700">
                                            Vidéo disponible
                                        </p>
                                        {videoFileName && (
                                            <p className="text-xs text-gray-500 font-mono">
                                                📁 {videoFileName}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <button
                                        onClick={handleDeleteVideo}
                                        disabled={loading}
                                        className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                    >
                                        🗑️ Supprimer
                                    </button>
                                </div>
                            </div>
                            <video
                                key={videoUrl}
                                controls
                                preload="metadata"
                                className="w-full max-h-80 rounded-lg bg-black"
                            >
                                <source src={`${videoUrl}`} />
                                Votre navigateur ne supporte pas la lecture vidéo.
                            </video>
                            <p className="text-xs text-gray-500">
                                💡 La lecture utilise le streaming avec support du seek (avance/retour dans la vidéo).
                            </p>
                        </div>
                    )}

                    <form onSubmit={handleVideoUpload} className="space-y-3">
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-rose-500 transition-colors">
                            <input
                                ref={videoInputRef}
                                type="file"
                                accept="video/mp4,video/webm"
                                onChange={(e) => {
                                    setVideoFile(e.target.files[0]);
                                    setError(null);
                                }}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-rose-600 file:text-white hover:file:bg-rose-700 cursor-pointer"
                            />
                            <p className="text-xs text-gray-400 mt-2">
                                Glissez-déposez votre vidéo (mp4 ou webm) ou cliquez pour sélectionner (max 500MB)
                            </p>
                            {videoFile && (
                                <p className="text-xs text-emerald-600 mt-2">
                                    ✅ Fichier sélectionné : {videoFile.name} ({Math.round(videoFile.size / 1024 / 1024)} MB)
                                </p>
                            )}
                        </div>

                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                        className="bg-rose-600 h-2.5 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Upload: {uploadProgress}%
                                </p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || !videoFile}
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Upload en cours...' : '📤 Uploader la vidéo'}
                        </button>
                    </form>

                    <div className="bg-blue-50 p-3 rounded-xl border border-blue-200">
                        <p className="text-xs text-blue-700">
                            💡 La vidéo sera stockée et accessible via streaming (lecture progressive, seek supporté).
                        </p>
                    </div>
                </div>
            )}

            {/* ======================================== */}
            {/* CONTENU JSON ou JSON_RICH */}
            {/* ======================================== */}
            {(contentType === 'JSON' || contentType === 'JSON_RICH') && (
                <form onSubmit={handleJsonSave} className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {contentType === 'JSON_RICH' ? '📝 Structure enrichie (JSON_RICH)' : '📝 Structure de formation (modèle JSON)'}
                            <span className="text-xs text-gray-400 ml-2">
                                {contentType === 'JSON_RICH' ? 'Format enrichi avec médias' : 'Format flexible pour la structure du module'}
                            </span>
                        </label>
                        <textarea
                            value={jsonContent}
                            onChange={(e) => setJsonContent(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            rows="15"
                            placeholder={`{
  "titre": "Introduction",
  "description": "Description du module",
  "modules": [
    {
      "titre": "Module 1",
      "lecons": [...]
    }
  ]
}`}
                        />
                        <div className="flex justify-between items-center mt-1">
                            <p className="text-xs text-gray-400">
                                Structure JSON flexible pour le contenu du module
                            </p>
                            <button
                                type="button"
                                onClick={validateJson}
                                className="text-xs text-green-600 hover:text-green-800 hover:underline"
                            >
                                🔍 Valider le JSON
                            </button>
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Sauvegarde...' : `💾 Sauvegarder le ${contentType}`}
                    </button>
                </form>
            )}

            {/* Bouton Démo */}
            <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <span className="text-sm text-gray-500">
                        🚀 Visualiser le contenu en mode démo
                    </span>
                    <button
                        onClick={handleOpenDemo}
                        disabled={!module}
                        className="px-6 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        👁️ Voir en démo
                    </button>
                </div>
                {!module && (
                    <p className="text-xs text-amber-600 mt-1">
                        ⚠️ Les informations du module sont nécessaires pour la démo
                    </p>
                )}
            </div>
        </div>
    );
}